import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Database, Play, ShieldCheck, ExternalLink, Eye, EyeOff, KeyRound } from 'lucide-react';
import { instances, instanzAdressen, sicherungen, type SqlErgebnis } from '../api';
import type { Instance } from '../types';
import CodeFeld from './CodeFeld';

/**
 * SQL gegen die Datenbank einer Instanz.
 *
 * Der Ersatz für „mal eben pgAdmin" — mit dem Unterschied, dass jeder Aufruf im
 * Protokoll steht und dass Schreiben nicht nebenbei passiert.
 *
 * Drei Zustände, und die Reihenfolge ist der Sinn der Sache:
 *
 *   Lesen       Vorgabe. Postgres läuft in einer Transaktion mit
 *               `SET TRANSACTION READ ONLY` und weist jeden Schreibversuch ab —
 *               auch den, der als `WITH … (DELETE …) SELECT` verkleidet ist.
 *   Probelauf   Führt aus, zeigt „UPDATE 3", und macht am Ende ROLLBACK.
 *               Man sieht, was passieren *würde*, bevor es passiert.
 *   Schreiben   Erst danach erreichbar, und nur mit getipptem Stack-Namen.
 *
 * Der getippte Name wird auch serverseitig geprüft. Was Daten ändert, darf
 * nicht daran hängen, dass die Oberfläche mitspielt.
 */
export default function SqlKonsole({ instanz }: { instanz: Instance }) {
  const { t } = useTranslation();
  const erwartet = instanz.stackName ?? instanz.id;

  const [sql, setSql] = useState('SELECT code, name FROM mandanten ORDER BY code;');
  const [schreibmodus, setSchreibmodus] = useState(false);
  const [getippt, setGetippt] = useState('');
  const [ergebnis, setErgebnis] = useState<SqlErgebnis | null>(null);
  const [fehler, setFehler] = useState('');
  /** Ein Probelauf, der durchlief — erst danach darf geschrieben werden. */
  const [probeOk, setProbeOk] = useState(false);

  /**
   * Die pgAdmin-Adresse dieser Instanz, falls es eine gibt.
   *
   * Der Verweis steht hier und nicht mehr bei den Adressen: wer pgAdmin sucht,
   * will an die Datenbank — und das ist dieser Reiter. Bei den Adressen bleibt
   * er als gewöhnlicher Eintrag stehen, denn dort gehört er als Adresse hin.
   *
   * Erkannt am `forwardHost`, nicht an der Notiz: die Notiz ist freier Text,
   * den jemand tippt oder auch nicht. Der Zielcontainer heisst dagegen immer
   * `…-pgadmin-…`, weil die Vorlage ihn so nennt.
   */
  const { data: adressen } = useQuery({
    queryKey: ['instanz-adressen', instanz.id],
    queryFn: () => instanzAdressen.list(instanz.id),
  });
  const pgadmin = adressen?.weitere.find((a) => /pgadmin/i.test(a.forwardHost));

  /**
   * Die Verbindungsdaten zur Datenbank.
   *
   * pgAdmin verbindet sich von selbst — `servers.json` und `pgpass` legt der
   * PC beim Ausrollen hinein. Gebraucht werden sie trotzdem: fuer jedes andere
   * Werkzeug, das ueber den Tunnel drangeht, und wenn jemand in pgAdmin eine
   * zweite Verbindung anlegt.
   *
   * Erst auf Klick geladen: der Abruf holt das Passwort vom Wirt und wird
   * protokolliert. Ihn bei jedem Oeffnen des Reiters auszuloesen hiesse, das
   * Protokoll mit Aufrufen zu fuellen, hinter denen niemand etwas wollte.
   */
  const [zugangOffen, setZugangOffen] = useState(false);
  const [passwortSichtbar, setPasswortSichtbar] = useState(false);
  const { data: zugang } = useQuery({
    queryKey: ['datenbankzugang', instanz.id],
    queryFn: () => sicherungen.datenbankzugang(instanz.id),
    enabled: zugangOffen,
  });

  const lauf = useMutation({
    mutationFn: (modus: 'lesen' | 'probelauf' | 'schreiben') =>
      instances.sql(instanz.id, sql, modus, getippt.trim()),
    onSuccess: (r) => {
      setFehler('');
      setErgebnis(r);
      if (r.modus === 'probelauf') setProbeOk(r.ok);
      if (r.modus === 'schreiben') {
        setProbeOk(false);
        setGetippt('');
      }
    },
    onError: (err: unknown) => {
      setErgebnis(null);
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          t('sql.failed'),
      );
    },
  });

  // Jede Änderung am Text entwertet den Probelauf: der galt für das, was da
  // stand, nicht für das, was jetzt dasteht.
  const sqlAendern = (w: string) => {
    setSql(w);
    setProbeOk(false);
  };

  const laeuft = lauf.isPending;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-surface p-5">
        <div className="mb-1 flex items-center gap-2">
          <Database size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">{t('sql.title')}</h3>
        </div>
        <p className="mb-3 text-xs text-slate-500">{t('sql.intro')}</p>

        {/* Der Weg zu pgAdmin, wo man ihn sucht: bei der Datenbank. Die
            Konsole hier protokolliert jeden Aufruf — pgAdmin nicht, und
            deshalb steht es daneben und nicht davor. */}
        {pgadmin && (
          <a
            href={`https://${pgadmin.hostname}`}
            target="_blank"
            rel="noreferrer"
            className="mb-3 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50"
          >
            <Database size={14} className="shrink-0 text-slate-500" />
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-medium text-slate-800">pgAdmin öffnen</span>
              <span className="block truncate font-mono text-[11px] text-slate-400">
                {pgadmin.hostname}
              </span>
            </span>
            <ExternalLink size={12} className="shrink-0 text-slate-400" />
          </a>
        )}

        {/* Die Verbindungsdaten, fuer alles ausser pgAdmin. */}
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setZugangOffen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700"
          >
            <KeyRound size={12} />
            {zugangOffen ? 'Verbindungsdaten ausblenden' : 'Verbindungsdaten anzeigen'}
          </button>

          {zugangOffen && zugang && (
            <div className="mt-2 space-y-2">
              {zugang.zugaenge.map((z) => (
                <div key={z.dienst} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <p className="text-[11px] font-medium text-slate-700">
                    {z.dienst} <span className="font-normal text-slate-400">({z.motor})</span>
                  </p>
                  <dl className="mt-1.5 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[11px]">
                    <dt className="text-slate-500">Datenbank</dt>
                    <dd className="font-mono text-slate-700">{z.datenbank}</dd>
                    <dt className="text-slate-500">Benutzer</dt>
                    <dd className="font-mono text-slate-700">{z.benutzer}</dd>
                    <dt className="text-slate-500">Passwort</dt>
                    <dd className="font-mono text-slate-700">
                      <button
                        type="button"
                        onClick={() => setPasswortSichtbar((v) => !v)}
                        className="inline-flex items-center gap-1.5 hover:text-brand-700"
                      >
                        {passwortSichtbar ? (z.passwort ?? '—') : '••••••••••'}
                        {passwortSichtbar ? <EyeOff size={11} /> : <Eye size={11} />}
                      </button>
                    </dd>
                    <dt className="text-slate-500">Im Stack</dt>
                    <dd className="font-mono text-slate-700">{z.intern}</dd>
                    {z.loopbackPort && (
                      <>
                        <dt className="text-slate-500">Über den Tunnel</dt>
                        <dd className="font-mono text-slate-700">
                          {zugang.tunnelAdresse}:{z.loopbackPort}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              ))}
              <p className="text-[11px] leading-snug text-slate-400">
                „Im Stack" gilt für Container auf demselben Wirt. „Über den Tunnel" gilt
                für Werkzeuge im Netz des Provisioning Centers — dorthin meldet der Wirt
                seinen Datenbankport, ohne ihn nach außen zu öffnen.
              </p>
            </div>
          )}
        </div>

        <CodeFeld sprache="sql" zeilen={8} wert={sql} onChange={sqlAendern} />

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={laeuft || !sql.trim()}
            onClick={() => lauf.mutate('lesen')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-40"
          >
            <Play size={13} /> {laeuft ? t('sql.running') : t('sql.read')}
          </button>

          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={schreibmodus}
              onChange={(e) => {
                setSchreibmodus(e.target.checked);
                setProbeOk(false);
                setGetippt('');
              }}
            />
            {t('sql.writeMode')}
          </label>

          {!schreibmodus && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
              <ShieldCheck size={13} /> {t('sql.readOnlyHint')}
            </span>
          )}
        </div>

        {schreibmodus && (
          <div className="mt-3 space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-start gap-2 text-xs text-amber-900">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>{t('sql.writeWarning')}</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={laeuft || !sql.trim()}
                onClick={() => lauf.mutate('probelauf')}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400 bg-surface px-3 py-2 text-sm text-amber-800 hover:bg-amber-100 disabled:opacity-40"
              >
                {t('sql.dryRun')}
              </button>

              {/* Erst nach einem gelungenen Probelauf. Wer schreiben will, hat
                  dann gesehen, wie viele Zeilen es trifft. */}
              {probeOk && (
                <>
                  <input
                    value={getippt}
                    onChange={(e) => setGetippt(e.target.value)}
                    placeholder={erwartet}
                    spellCheck={false}
                    className="rounded border border-amber-300 px-2 py-1.5 font-mono text-xs"
                  />
                  <button
                    type="button"
                    disabled={laeuft || getippt.trim() !== erwartet}
                    onClick={() => lauf.mutate('schreiben')}
                    className="rounded-lg bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-40"
                  >
                    {t('sql.write')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {fehler && (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{fehler}</div>
        )}
      </div>

      {ergebnis && (
        <div className="rounded-xl border border-slate-200 bg-surface p-5">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-slate-700">
              {t(`sql.mode_${ergebnis.modus}`)}
              {ergebnis.ok ? '' : ` — ${t('sql.aborted')}`}
            </span>
            <span className="text-xs text-slate-400">
              {t('sql.rows', { count: ergebnis.zeilen.length })} · {ergebnis.dauerMs} ms
            </span>
          </div>

          {ergebnis.gekuerzt && (
            <div className="mb-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
              {t('sql.truncated')}
            </div>
          )}

          {ergebnis.spalten.length > 0 && (
            <div className="overflow-x-auto rounded border border-slate-200">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    {ergebnis.spalten.map((s) => (
                      <th key={s} className="px-2 py-1.5 text-left font-medium text-slate-600">
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ergebnis.zeilen.map((z, i) => (
                    <tr key={i}>
                      {z.map((w, j) => (
                        <td key={j} className="whitespace-pre px-2 py-1 font-mono text-slate-700">
                          {w === '' ? <span className="text-slate-300">∅</span> : w}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Befehlsmarken und Fehlertexte. Getrennt von der Tabelle, weil eine
              Fehlermeldung als Tabellenzeile niemand liest. */}
          {ergebnis.meldung && (
            <pre
              className={`mt-2 overflow-x-auto rounded p-3 text-xs ${
                ergebnis.ok ? 'bg-konsole text-konsole-hell' : 'bg-red-50 text-red-800'
              }`}
            >
              {ergebnis.meldung}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
